/**
 * Candlestick pattern detection (OHLC). Green/red markers via buildPatternMarkers.
 */

const body = (c) => Math.abs(c.close - c.open);
const upperWick = (c) => c.high - Math.max(c.open, c.close);
const lowerWick = (c) => Math.min(c.open, c.close) - c.low;
const isBull = (c) => c.close > c.open;

export const DEFAULT_PATTERN_TOGGLES = {
  doji: true,
  hammer: true,
  engulfing: true,
  morningEvening: true,
  threeSoldiers: true,
};

function detectDoji(candles, i) {
  const c = candles[i];
  const range = c.high - c.low;
  if (range <= 0) return null;
  const b = body(c);
  if (b / range < 0.1) return { id: "doji", bias: "neutral", label: "Doji" };
  return null;
}

function detectHammer(candles, i) {
  const c = candles[i];
  const range = c.high - c.low;
  if (range <= 0) return null;
  const b = body(c);
  const uw = upperWick(c);
  const lw = lowerWick(c);
  if (lw > b * 2 && uw < b * 0.5 && b / range < 0.35) {
    return {
      id: "hammer",
      bias: isBull(c) ? "bullish" : "bearish",
      label: isBull(c) ? "Hammer" : "Hanging Man",
    };
  }
  return null;
}

function detectEngulfing(candles, i) {
  if (i < 1) return null;
  const cur = candles[i];
  const prev = candles[i - 1];
  const b0 = body(prev);
  const b1 = body(cur);
  if (b0 <= 0 || b1 <= 0) return null;
  if (isBull(prev) && !isBull(cur) && prev.open <= cur.close && prev.close >= cur.open && b1 > b0) {
    return { id: "bearEngulf", bias: "bearish", label: "Bear Engulf" };
  }
  if (!isBull(prev) && isBull(cur) && prev.open >= cur.close && prev.close <= cur.open && b1 > b0) {
    return { id: "bullEngulf", bias: "bullish", label: "Bull Engulf" };
  }
  return null;
}

function detectMorningStar(candles, i) {
  if (i < 2) return null;
  const a = candles[i - 2];
  const b = candles[i - 1];
  const c = candles[i];
  if (!isBull(a) && body(a) > body(b) && c.close > (a.open + a.close) / 2 && isBull(c)) {
    return { id: "morning", bias: "bullish", label: "Morning Star" };
  }
  if (isBull(a) && body(a) > body(b) && c.close < (a.open + a.close) / 2 && !isBull(c)) {
    return { id: "evening", bias: "bearish", label: "Evening Star" };
  }
  return null;
}

function detectThreeSoldiers(candles, i) {
  if (i < 2) return null;
  const a = candles[i - 2];
  const b = candles[i - 1];
  const c = candles[i];
  if (isBull(a) && isBull(b) && isBull(c) && c.close > b.close && b.close > a.close) {
    return { id: "threeWhite", bias: "bullish", label: "3 White Soldiers" };
  }
  if (!isBull(a) && !isBull(b) && !isBull(c) && c.close < b.close && b.close < a.close) {
    return { id: "threeBlack", bias: "bearish", label: "3 Black Crows" };
  }
  return null;
}

/**
 * @returns {Map<number, Array<{id: string, bias: string, label: string}>>}
 */
export function scanAllPatterns(candles, toggles = DEFAULT_PATTERN_TOGGLES) {
  const map = new Map();
  if (!candles?.length) return map;

  for (let i = 0; i < candles.length; i += 1) {
    const found = [];
    if (toggles.doji) {
      const p = detectDoji(candles, i);
      if (p) found.push(p);
    }
    if (toggles.hammer) {
      const p = detectHammer(candles, i);
      if (p) found.push(p);
    }
    if (toggles.engulfing) {
      const p = detectEngulfing(candles, i);
      if (p) found.push(p);
    }
    if (toggles.morningEvening) {
      const p = detectMorningStar(candles, i);
      if (p) found.push(p);
    }
    if (toggles.threeSoldiers) {
      const p = detectThreeSoldiers(candles, i);
      if (p) found.push(p);
    }
    if (found.length) map.set(candles[i].time, found);
  }
  return map;
}

export function buildPatternMarkers(patternMapByTime, candles, options = {}) {
  const bullishColor = options.bullishColor ?? "#16a34a";
  const bearishColor = options.bearishColor ?? "#dc2626";
  const neutralColor = options.neutralColor ?? "#94a3b8";
  const markers = [];

  const candleByTime = new Map(candles.map((c) => [c.time, c]));

  patternMapByTime.forEach((patterns, time) => {
    const c = candleByTime.get(time);
    if (!c || !patterns?.length) return;
    const label = patterns.map((p) => p.label).join(", ");
    const bias = patterns.some((p) => p.bias === "bullish")
      ? "bullish"
      : patterns.some((p) => p.bias === "bearish")
        ? "bearish"
        : "neutral";
    const color =
      bias === "bullish"
        ? bullishColor
        : bias === "bearish"
          ? bearishColor
          : neutralColor;
    markers.push({
      time,
      position: bias === "bearish" ? "aboveBar" : "belowBar",
      color,
      shape: "circle",
      text: label,
      size: 0.8,
      price: bias === "bearish" ? c.high : c.low,
    });
  });

  return markers;
}
